import { useEffect, useState, useCallback } from "react";
import { KeyRound, ShieldCheck, Clock, Smartphone, History, Loader2, Trash2, Monitor, CheckCircle2, MailWarning, Lock, Globe, FileText, Settings2, AlertTriangle, Eraser, CreditCard, Pin, XCircle, Zap, Pencil, DollarSign, IdCard, ArrowLeftRight, UserPlus, Cog, TrendingUp, ChevronDown } from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { useAdminAuth } from "./AdminAuth";
import { toast } from "sonner";
import { PageHeader } from "./shared";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { GeographicPanel } from "./GeographicControl";
import { ContentPanel } from "./ContentManagement";
import AppUpdateSettings from "./AppUpdateSettings";

const TIMEOUTS = [15, 30, 60];

const Section = ({ icon: Icon, title, desc, children, collapsible = false, defaultOpen = true, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = !collapsible || open;
  return (
    <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 shadow-sm" data-testid={testId}>
      {collapsible ? (
        <button
          type="button"
          data-testid={`${testId}-toggle`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={`flex w-full items-start gap-3 text-left ${open ? "mb-4" : ""}`}
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#2340b5]/8"><Icon className="h-5 w-5 text-[#2340b5]" /></span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg font-bold text-[#12142b]">{title}</h2>
            {desc && <p className="text-sm text-[#565b73]">{desc}</p>}
          </div>
          <ChevronDown className={`mt-2 h-5 w-5 shrink-0 text-[#565b73] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      ) : (
        <div className="mb-4 flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#2340b5]/8"><Icon className="h-5 w-5 text-[#2340b5]" /></span>
          <div>
            <h2 className="font-display text-lg font-bold text-[#12142b]">{title}</h2>
            {desc && <p className="text-sm text-[#565b73]">{desc}</p>}
          </div>
        </div>
      )}
      {expanded && children}
    </div>
  );
};

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) return toast.error("New password must be at least 8 characters");
    if (nw !== confirm) return toast.error("New password and confirmation do not match");
    setLoading(true);
    try {
      await adminApi.post("/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated successfully");
      setCur(""); setNw(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally {
      setLoading(false);
    }
  };

  const field = (label, value, onChange, testid) => (
    <div>
      <label className="mb-1 block text-sm font-semibold text-[#12142b]">{label}</label>
      <input
        type="password" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} required
        className="w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
      />
    </div>
  );

  return (
    <Section icon={KeyRound} title="Change Password" desc="Update the password for your admin account.">
      <form onSubmit={submit} className="grid max-w-xl gap-4">
        {field("Current password", cur, setCur, "current-password")}
        {field("New password", nw, setNw, "new-password")}
        {field("Confirm new password", confirm, setConfirm, "confirm-password")}
        <div>
          <button data-testid="change-password-submit" disabled={loading} className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update Password
          </button>
        </div>
      </form>
    </Section>
  );
}

function SecurityPanel() {
  const [settings, setSettings] = useState(null);
  const [devices, setDevices] = useState(null);
  const [history, setHistory] = useState(null);
  const [twofaEmail, setTwofaEmail] = useState("");
  const [twofaPhone, setTwofaPhone] = useState("");
  const [savingDest, setSavingDest] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/security/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ session_timeout_min: 30, two_factor_enabled: false }));
    adminApi.get("/security/devices").then((r) => setDevices(r.data.items || [])).catch(() => setDevices([]));
    adminApi.get("/security/login-history").then((r) => setHistory(r.data.items || [])).catch(() => setHistory([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (settings) {
      setTwofaEmail(settings.twofa_email || "");
      setTwofaPhone(settings.twofa_phone || "");
    }
  }, [settings]);

  const saveDest = async () => {
    setSavingDest(true);
    try {
      const { data } = await adminApi.patch("/security/settings", { twofa_email: twofaEmail, twofa_phone: twofaPhone });
      setSettings(data);
      toast.success("Verification destinations saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save destinations");
    } finally {
      setSavingDest(false);
    }
  };

  const patch = async (body, label) => {
    try {
      const { data } = await adminApi.patch("/security/settings", body);
      setSettings(data);
      window.dispatchEvent(new Event("sr-security-settings-changed"));
      toast.success(label);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update setting");
    }
  };

  const revoke = async (id, name) => {
    try {
      await adminApi.delete(`/security/trusted-devices/${id}`);
      toast.success(`Removed ${name}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove device");
    }
  };

  const fmt = (s) => s ? new Date(s).toLocaleString("en-CA", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="space-y-6" data-testid="security-panel">
      <ChangePassword />

        {/* Session security */}
        <Section icon={Clock} title="Session Security" desc="Automatically sign out after a period of inactivity.">
          <div className="flex flex-wrap gap-2" data-testid="session-timeout-options">
            {TIMEOUTS.map((m) => {
              const active = settings?.session_timeout_min === m;
              return (
                <button
                  key={m} data-testid={`session-timeout-${m}`}
                  onClick={() => patch({ session_timeout_min: m }, `Auto sign-out set to ${m} minutes`)}
                  className={`rounded-xl border px-5 py-2.5 text-sm font-semibold transition-colors ${active ? "border-[#2340b5] bg-[#2340b5] text-white" : "border-[#12142b]/12 bg-white text-[#12142b] hover:bg-[#f4f6fb]"}`}
                >
                  {m} min
                </button>
              );
            })}
          </div>
        </Section>

        {/* Two-factor */}
        <Section icon={ShieldCheck} title="Two-Factor Authentication" desc="Require a verification code when signing in from an unrecognized device.">
          <div className="flex items-center justify-between rounded-xl bg-[#f4f6fb] p-4">
            <div>
              <p className="text-sm font-semibold text-[#12142b]">{settings?.two_factor_enabled ? "Enabled" : "Disabled"}</p>
              <p className="text-xs text-[#565b73]">Trusted devices skip the code. Admin trust expires after 30 days; super-admin trust lasts until revoked manually.</p>
            </div>
            <Switch
              data-testid="two-factor-toggle"
              checked={!!settings?.two_factor_enabled}
              onCheckedChange={(v) => patch({ two_factor_enabled: v }, v ? "Two-factor enabled" : "Two-factor disabled")}
            />
          </div>
          {settings?.two_factor_enabled && (
            <div className="mt-4 max-w-xl rounded-xl border border-[#12142b]/8 p-4" data-testid="twofa-destinations-panel">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#565b73]">Where codes are sent</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-[#12142b]">Email</label>
                  <input
                    data-testid="twofa-email-input" type="email" value={twofaEmail} onChange={(e) => setTwofaEmail(e.target.value)}
                    className="w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-[#12142b]">Phone (SMS)</label>
                  <input
                    data-testid="twofa-phone-input" type="tel" value={twofaPhone} onChange={(e) => setTwofaPhone(e.target.value)} placeholder="+13065550123"
                    className="w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
                  />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <button data-testid="twofa-destination-save" onClick={saveDest} disabled={savingDest}
                  className="flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
                  {savingDest && <Loader2 className="h-4 w-4 animate-spin" />} Save destinations
                </button>
                <p className="text-xs text-[#565b73]">Codes are emailed instantly; SMS sends too once a phone is set.</p>
              </div>
            </div>
          )}
        </Section>

        {/* Login alerts */}
        <Section icon={MailWarning} title="Login Alerts" desc="Email me whenever a new device signs into the admin panel.">
          <div className="flex items-center justify-between rounded-xl bg-[#f4f6fb] p-4">
            <div>
              <p className="text-sm font-semibold text-[#12142b]">{settings?.login_alerts_enabled ? "On" : "Off"}</p>
              <p className="text-xs text-[#565b73]">Alerts are sent to your admin email for any unrecognized device.</p>
            </div>
            <Switch
              data-testid="login-alerts-toggle"
              checked={!!settings?.login_alerts_enabled}
              onCheckedChange={(v) => patch({ login_alerts_enabled: v }, v ? "Login alerts on" : "Login alerts off")}
            />
          </div>
        </Section>

        {/* Trusted devices */}
        <Section icon={Smartphone} title="Trusted Devices" desc="Devices allowed to skip extra verification.">
          <DeviceList
            testid="trusted-devices"
            items={(devices || []).filter((d) => d.trusted)}
            loading={devices === null}
            empty="No trusted devices yet."
            fmt={fmt}
            onRevoke={revoke}
          />
        </Section>

        {/* Device history */}
        <Section icon={Monitor} title="Device History" desc="All devices that have accessed this account.">
          <DeviceList testid="device-history" items={devices || []} loading={devices === null} empty="No device activity recorded yet." fmt={fmt} />
        </Section>

        {/* Login history */}
        <Section icon={History} title="Login History" desc="Recent sign-in activity.">
          {history === null && <p className="py-6 text-center text-sm text-[#565b73]">Loading…</p>}
          {history !== null && history.length === 0 && <p className="py-6 text-center text-sm text-[#565b73]" data-testid="login-history-empty">No login history recorded yet.</p>}
          {history && history.length > 0 && (
            <table className="w-full text-left text-sm" data-testid="login-history-table">
              <thead><tr className="border-b border-[#12142b]/8 text-[10px] uppercase tracking-wide text-[#565b73]"><th className="py-2 pr-2">When</th><th className="py-2 pr-2">User</th><th className="py-2 pr-2">IP / Location</th><th className="py-2 pr-2">Device</th><th className="py-2 pr-2 text-right">Status</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-[#12142b]/5 last:border-0">
                    <td className="py-2.5 pr-2 text-[#565b73]">{fmt(h.at)}</td>
                    <td className="py-2.5 pr-2 text-[#12142b]">{h.user}</td>
                    <td className="py-2.5 pr-2 text-[#565b73]">{h.ip}{h.location ? ` · ${h.location}` : ""}</td>
                    <td className="py-2.5 pr-2 text-[#565b73]">{h.device}</td>
                    <td className="py-2.5 pr-2 text-right"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${h.status === "success" ? "bg-[#16a34a]/12 text-[#16a34a]" : "bg-[#e4002b]/12 text-[#e4002b]"}`}>{h.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
    </div>
  );
}

const AUTH_ICONS = {
  payouts_over: DollarSign,
  verify_user_id: IdCard,
  view_documents: FileText,
  etransfers_over: ArrowLeftRight,
  driver_accounts: UserPlus,
  system_settings: Cog,
  fraud_alerts: AlertTriangle,
  financial_reports: TrendingUp,
};

function AuthorizationPanel() {
  const [rules, setRules] = useState(null);
  const [edit, setEdit] = useState(null); // { key, label, value }
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/authorization/rules").then((r) => setRules(r.data.items)).catch(() => setRules([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (body, msg) => {
    try {
      const { data } = await adminApi.patch("/authorization/rules", body);
      setRules(data.items);
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update authorization rule");
    }
  };

  const saveThreshold = async () => {
    const v = parseInt(edit.value, 10);
    if (Number.isNaN(v) || v < 200 || v > 500) return toast.error("Threshold must be between $200 and $500");
    setSaving(true);
    await patch({ key: edit.key, threshold: v }, "Authorization threshold updated");
    setSaving(false);
    setEdit(null);
  };

  if (rules === null) return null;

  return (
    <Section icon={ShieldCheck} title="Authorization Access" desc="Choose which sensitive actions employees can only perform with your approval. Approvals are one-time and expire 15 minutes after you authorize.">
      <div data-testid="authorization-panel">
        <div className="grid grid-cols-[1fr_auto] gap-4 border-b border-[#12142b]/10 pb-2 text-xs font-bold uppercase tracking-wide text-[#565b73]">
          <span>Action / Information</span><span>Requires Authorization</span>
        </div>
        {rules.map((r) => {
          const Icon = AUTH_ICONS[r.key] || ShieldCheck;
          return (
            <div key={r.key} data-testid={`auth-rule-${r.key}`} className="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-[#12142b]/6 py-4 last:border-0">
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#2340b5]/8"><Icon className="h-5 w-5 text-[#2340b5]" /></span>
                <div>
                  <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-[#12142b]">
                    {r.label}
                    {r.has_threshold && (
                      <button data-testid={`auth-rule-threshold-edit-${r.key}`} onClick={() => setEdit({ key: r.key, label: r.label.replace(/\s*over\s*\$\d+/, ""), value: r.threshold })}
                        className="inline-flex items-center gap-1 rounded-md bg-[#2340b5]/8 px-2 py-0.5 text-xs font-semibold text-[#2340b5] hover:bg-[#2340b5]/15">
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-[#565b73]">{r.desc}</p>
                </div>
              </div>
              <Switch data-testid={`auth-rule-toggle-${r.key}`} checked={!!r.enabled}
                className="h-6 w-11 border border-[#12142b]/10 data-[state=checked]:bg-[#16a34a] data-[state=unchecked]:bg-[#e4002b] [&>span]:h-5 [&>span]:w-5 [&>span]:bg-white data-[state=checked]:[&>span]:translate-x-5"
                onCheckedChange={(v) => patch({ key: r.key, enabled: v }, `${r.label} ${v ? "now requires" : "no longer requires"} authorization`)} />
            </div>
          );
        })}
      </div>

      {/* Threshold edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-sm" data-testid="auth-threshold-dialog">
          <DialogHeader><DialogTitle>Edit threshold — {edit?.label}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#565b73]">Actions above this amount will require admin authorization. Allowed range: $200 – $500.</p>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 focus-within:border-[#2340b5]">
            <span className="text-sm font-semibold text-[#565b73]">$</span>
            <input
              data-testid="auth-threshold-input" type="number" min="200" max="500" step="10"
              value={edit?.value ?? ""} onChange={(e) => setEdit({ ...edit, value: e.target.value })}
              className="w-full bg-transparent py-3 text-sm text-[#12142b] outline-none"
            />
            <span className="text-sm font-semibold text-[#565b73]">CAD</span>
          </div>
          <DialogFooter className="mt-3 gap-2 sm:justify-between">
            <button data-testid="auth-threshold-cancel" onClick={() => setEdit(null)} disabled={saving}
              className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Cancel</button>
            <button data-testid="auth-threshold-save" onClick={saveThreshold} disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function GeneralPanel() {
  const { admin } = useAdminAuth();
  const [summary, setSummary] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/maintenance/test-accounts-summary")
      .then((r) => setSummary(r.data))
      .catch(() => setSummary({ test_accounts: 0, open_rides: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await adminApi.post("/maintenance/purge-test-accounts");
      const deleted = data.deleted_upstream ?? 0;
      const failed = data.delete_failed ?? 0;
      if (failed > 0) {
        toast.warning(`Deleted ${deleted} test account(s) — ${failed} could not be removed and are still counted`, { duration: 8000 });
      } else {
        toast.success(`Permanently deleted ${deleted} test account(s) from the platform`);
      }
      setConfirmOpen(false);
      load(); // re-query so the counters update immediately
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="general-panel">
      {/* Authorization access (super admin only) */}
      {admin?.role === "super_admin" && <AuthorizationPanel />}

      {/* Danger zone */}
      <div className="rounded-2xl border border-[#e4002b]/25 bg-white p-6 shadow-sm" data-testid="danger-zone">
        <div className="mb-4 flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#e4002b]/10"><AlertTriangle className="h-5 w-5 text-[#e4002b]" /></span>
          <div>
            <h2 className="font-display text-lg font-bold text-[#12142b]">Test Data Cleanup</h2>
            <p className="text-sm text-[#565b73]">One-tap production reset — permanently deletes every test account and all of their data (rides, bookings, messages, wallet activity) from the platform.</p>
          </div>
        </div>

        <div className="mb-5 grid max-w-md grid-cols-2 gap-3">
          <div className="rounded-xl bg-[#f4f6fb] p-4" data-testid="purge-summary-accounts">
            <p className="text-2xl font-bold text-[#12142b]">{summary === null ? "…" : summary.test_accounts}</p>
            <p className="text-xs text-[#565b73]">Flagged test accounts</p>
          </div>
          <div className="rounded-xl bg-[#f4f6fb] p-4" data-testid="purge-summary-rides">
            <p className="text-2xl font-bold text-[#12142b]">{summary === null ? "…" : summary.open_rides}</p>
            <p className="text-xs text-[#565b73]">Upcoming rides by test accounts</p>
          </div>
        </div>

        <button
          data-testid="purge-test-accounts-button"
          onClick={() => setConfirmOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-[#e4002b] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#b80023]"
        >
          <Eraser className="h-4 w-4" /> Delete all test accounts &amp; their data
        </button>
        <p className="mt-3 text-xs text-[#565b73]">Runs the platform-wide purge on maple-rides. This cannot be undone — production accounts are never touched.</p>
      </div>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="purge-confirm-dialog">
          <DialogHeader><DialogTitle>Delete all test accounts?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-[#565b73]">
            <p>This will permanently remove <span className="font-semibold text-[#12142b]">every test account and all of their data</span> from the platform — rides, bookings, messages and wallet activity.</p>
            {summary && (
              <p data-testid="purge-confirm-counts">
                Currently flagged: <span className="font-semibold text-[#12142b]">{summary.test_accounts} test account{summary.test_accounts === 1 ? "" : "s"}</span>
                {summary.open_rides > 0 && <> with <span className="font-semibold text-[#12142b]">{summary.open_rides} upcoming ride{summary.open_rides === 1 ? "" : "s"}</span></>}.
              </p>
            )}
            <p className="rounded-xl bg-[#e4002b]/8 p-3 text-xs font-semibold text-[#e4002b]">This action is irreversible. Real production accounts are not affected.</p>
          </div>
          <DialogFooter className="mt-2 gap-2 sm:justify-between">
            <button data-testid="purge-cancel-button" onClick={() => setConfirmOpen(false)} disabled={purging}
              className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Cancel</button>
            <button data-testid="purge-confirm-button" onClick={purge} disabled={purging}
              className="inline-flex items-center gap-2 rounded-xl bg-[#e4002b] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#b80023] disabled:opacity-60">
              {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {purging ? "Purging…" : "Yes, delete everything"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FinNote({ children, testid }) {
  return (
    <div className="mt-4 border-l-4 border-[#12142b]/15 bg-[#f4f6fb] px-4 py-3 text-sm text-[#565b73]" data-testid={testid}>
      {children}
    </div>
  );
}

function FinRow({ label, value, action, testid }) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-[#12142b]/6 py-3.5 last:border-0" data-testid={testid}>
      <span className="text-sm font-medium text-[#12142b]">{label}</span>
      <span className="text-sm font-semibold text-[#12142b]">{value}</span>
      <span className="w-16 text-right">{action}</span>
    </div>
  );
}

function FinEditBtn({ onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="inline-flex items-center gap-1 text-sm font-semibold text-[#2340b5] hover:text-[#1b3390]">
      <Pencil className="h-3.5 w-3.5" /> Edit
    </button>
  );
}

function FinancePanel() {
  const [fin, setFin] = useState(null);
  const [edit, setEdit] = useState(null); // { field, label, value, kind: "pct" | "money" }
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/finance-settings").then((r) => setFin(r.data)).catch(() => setFin(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (body, msg) => {
    try {
      const { data } = await adminApi.patch("/finance-settings", body);
      setFin(data);
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save finance settings");
    }
  };

  const saveEdit = async () => {
    const v = parseFloat(edit.value);
    if (Number.isNaN(v) || v < 0) return toast.error("Enter a valid number");
    setSaving(true);
    await patch({ [edit.field]: v }, `${edit.label} updated`);
    setSaving(false);
    setEdit(null);
  };

  if (!fin) return <div className="py-12 text-center text-sm text-[#565b73]">Loading finance settings…</div>;
  const money = (v) => `$${Number(v).toFixed(2)}`;

  return (
    <div className="space-y-6" data-testid="finance-panel">
      {fin.source === "local" && (
        <div className="rounded-xl bg-[#f59e0b]/10 px-4 py-3 text-sm font-medium text-[#b45309]" data-testid="finance-local-note">
          Saved locally for now — these values will sync to the platform automatically once the maple-rides finance endpoints go live.
        </div>
      )}

      {/* Booking Fees */}
      <Section icon={CreditCard} title="Booking Fees" desc="Charged to riders on every booking." collapsible defaultOpen={false} testId="fin-booking-fees-section">
        <div className="grid grid-cols-[1fr_auto_auto] gap-4 border-b border-[#12142b]/10 pb-2 text-xs font-bold uppercase tracking-wide text-[#565b73]">
          <span>Setting</span><span>Current Value</span><span className="w-16 text-right">Action</span>
        </div>
        <FinRow testid="fin-booking-fee-row" label="Rider booking fee" value={`${fin.booking_fee.rider_pct}%`}
          action={<FinEditBtn testid="booking-fee-edit" onClick={() => setEdit({ field: "booking_fee_pct", label: "Rider booking fee", value: fin.booking_fee.rider_pct, kind: "pct" })} />} />
        <FinRow testid="fin-booking-fee-status-row" label="Fee status" value={fin.booking_fee.active ? "Active" : "Inactive"}
          action={<Switch data-testid="booking-fee-toggle" checked={!!fin.booking_fee.active} onCheckedChange={(v) => patch({ booking_fee_active: v }, v ? "Booking fee activated" : "Booking fee paused")} />} />
        <FinRow testid="fin-booking-fee-currency-row" label="Currency" value={fin.booking_fee.currency} action={<span className="text-sm text-[#565b73]">—</span>} />
        <FinNote testid="booking-fee-note">{fin.booking_fee.note}</FinNote>
      </Section>

      {/* Pricing */}
      <Section icon={DollarSign} title="Pricing" desc="Suggested per-kilometre price the app uses to price seats per trip — changes take effect immediately." collapsible defaultOpen={false} testId="fin-pricing-section">
        <div className="grid grid-cols-[1fr_auto_auto] gap-4 border-b border-[#12142b]/10 pb-2 text-xs font-bold uppercase tracking-wide text-[#565b73]">
          <span>Setting</span><span>Current Value</span><span className="w-16 text-right">Action</span>
        </div>
        <FinRow testid="fin-suggested-price-per-km-row" label="Suggested price per km" value={`$${Number(fin.pricing?.suggested_price_per_km ?? 0.11).toFixed(2)}`}
          action={<FinEditBtn testid="suggested-price-per-km-edit" onClick={() => setEdit({ field: "suggested_price_per_km", label: "Suggested price per km", value: fin.pricing?.suggested_price_per_km ?? 0.11, kind: "money" })} />} />
        <FinRow testid="fin-max-price-per-km-row" label="Max price per km" value={`$${Number(fin.pricing?.max_price_per_km ?? 0.15).toFixed(2)}`}
          action={<FinEditBtn testid="max-price-per-km-edit" onClick={() => setEdit({ field: "max_price_per_km", label: "Max price per km", value: fin.pricing?.max_price_per_km ?? 0.15, kind: "money" })} />} />
        <FinRow testid="fin-price-per-km-currency-row" label="Currency" value={fin.pricing?.currency || "CAD"} action={<span className="text-sm text-[#565b73]">—</span>} />
        <FinNote testid="price-per-km-note">{fin.pricing?.note}</FinNote>
      </Section>

      {/* Community Listing Pins */}
      <Section icon={Pin} title="Community Listing Pins" desc="Paid pinned placement inside community groups." collapsible defaultOpen={false} testId="fin-community-pins-section">
        <div className="grid grid-cols-[1fr_auto_auto] gap-4 border-b border-[#12142b]/10 pb-2 text-xs font-bold uppercase tracking-wide text-[#565b73]">
          <span>Setting</span><span>Current Value</span><span className="w-16 text-right">Action</span>
        </div>
        <FinRow testid="fin-pin-price-row" label="Pin listing price" value={money(fin.pins.price)}
          action={<FinEditBtn testid="pin-price-edit" onClick={() => setEdit({ field: "pin_price", label: "Pin listing price", value: fin.pins.price, kind: "money" })} />} />
        <FinRow testid="fin-pin-currency-row" label="Currency" value={fin.pins.currency} action={<span className="text-sm text-[#565b73]">—</span>} />
        <FinRow testid="fin-pin-status-row" label="Pin status" value={fin.pins.active ? "Active" : "Inactive"}
          action={<Switch data-testid="pin-status-toggle" checked={!!fin.pins.active} onCheckedChange={(v) => patch({ pin_active: v }, v ? "Listing pins activated" : "Listing pins paused")} />} />
        <FinNote testid="pin-note">{fin.pins.note}</FinNote>
      </Section>

      {/* Rider Cancellation policy */}
      <Section icon={XCircle} title="Rider Cancellation" desc="How trip charges are split when a rider cancels." collapsible defaultOpen={false} testId="fin-cancellation-section">
        <FinRow testid="fin-auto-approve-threshold-row" label="Auto-approve refunds under" value={`$${Number(fin.rider_cancellation?.auto_approve_threshold ?? 50).toFixed(2)}`}
          action={<FinEditBtn testid="auto-approve-threshold-edit" onClick={() => setEdit({ field: "refund_auto_approve_threshold", label: "Auto-approve refunds under", value: fin.rider_cancellation?.auto_approve_threshold ?? 50, kind: "money" })} />} />
        {["standard", "short_notice"].map((k) => {
          const p = fin.rider_cancellation[k];
          const raw = p.raw || {};
          return (
            <div key={k} className="mb-5 last:mb-0" data-testid={`cancel-policy-${k}`}>
              <h3 className="mb-2 font-display text-base font-bold text-[#12142b]">{p.title}</h3>
              <div className="grid grid-cols-[1fr_auto_auto] gap-4 border-b border-[#12142b]/10 pb-2 text-xs font-bold uppercase tracking-wide text-[#565b73]">
                <span>Setting</span><span>Value</span><span className="w-16 text-right">Action</span>
              </div>
              <FinRow testid={`cancel-${k}-deadline-row`} label="Cancellation deadline" value={p.deadline}
                action={<FinEditBtn testid={`cancel-${k}-deadline-edit`} onClick={() => setEdit({ field: `cancel_${k}_deadline_hours`, label: "Cancellation deadline", value: raw.deadline_hours, kind: "hours" })} />} />
              <FinRow testid={`cancel-${k}-driver-row`} label="Driver receives" value={p.driver_receives}
                action={<FinEditBtn testid={`cancel-${k}-driver-edit`} onClick={() => setEdit({ field: `cancel_${k}_driver_pct`, label: "Driver receives", value: raw.driver_pct, kind: "pct" })} />} />
              <FinRow testid={`cancel-${k}-rider-row`} label="Rider refund" value={p.rider_refund}
                action={<FinEditBtn testid={`cancel-${k}-rider-edit`} onClick={() => setEdit({ field: `cancel_${k}_rider_refund_pct`, label: "Rider refund", value: raw.rider_refund_pct, kind: "pct" })} />} />
              <FinRow testid={`cancel-${k}-fee-row`} label="Cancellation fee" value={p.cancellation_fee}
                action={<FinEditBtn testid={`cancel-${k}-fee-edit`} onClick={() => setEdit({ field: `cancel_${k}_flat_fee`, label: "Cancellation fee", value: raw.flat_fee, kind: "money" })} />} />
              <FinRow testid={`cancel-${k}-fee-refund-row`} label="Booking fee refund" value={p.booking_fee_refund}
                action={<Switch data-testid={`cancel-${k}-fee-refund-toggle`} checked={!!raw.refund_booking_fee} onCheckedChange={(v) => patch({ [`cancel_${k}_refund_booking_fee`]: v }, v ? "Booking fee will be refunded" : "Booking fee kept on cancellation")} />} />
            </div>
          );
        })}
        <FinNote testid="cancel-policy-note">Driver share and rider refund should add up to 100% — the platform validates percentages on save.</FinNote>
      </Section>

      {/* Fast-Cash E-Transfer */}
      <Section icon={Zap} title="Fast-Cash E-Transfer" desc="Instant payout option for drivers." collapsible defaultOpen={false} testId="fin-fastcash-section">
        <div className="grid grid-cols-[1fr_auto_auto] gap-4 border-b border-[#12142b]/10 pb-2 text-xs font-bold uppercase tracking-wide text-[#565b73]">
          <span>Member Type</span><span>Fee</span><span className="w-16 text-right">Action</span>
        </div>
        <FinRow testid="fin-fastcash-commute-row" label="Commute group member" value={money(fin.fast_cash.commute_fee)}
          action={<FinEditBtn testid="fastcash-commute-edit" onClick={() => setEdit({ field: "fastcash_commute_fee", label: "Commute group member fee", value: fin.fast_cash.commute_fee, kind: "money" })} />} />
        <FinRow testid="fin-fastcash-noncommute-row" label="Non-commute group member" value={money(fin.fast_cash.non_commute_fee)}
          action={<FinEditBtn testid="fastcash-noncommute-edit" onClick={() => setEdit({ field: "fastcash_noncommute_fee", label: "Non-commute group member fee", value: fin.fast_cash.non_commute_fee, kind: "money" })} />} />
        <FinRow testid="fin-fastcash-status-row" label="Service status" value={fin.fast_cash.active ? "Active" : "Inactive"}
          action={<Switch data-testid="fastcash-status-toggle" checked={!!fin.fast_cash.active} onCheckedChange={(v) => patch({ fastcash_active: v }, v ? "Fast-Cash activated" : "Fast-Cash paused")} />} />
      </Section>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-sm" data-testid="finance-edit-dialog">
          <DialogHeader><DialogTitle>Edit {edit?.label}</DialogTitle></DialogHeader>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 focus-within:border-[#2340b5]">
            {edit?.kind === "money" && <span className="text-sm font-semibold text-[#565b73]">$</span>}
            <input
              data-testid="finance-edit-input" type="number" min="0" step={edit?.kind === "pct" ? "1" : "0.01"}
              value={edit?.value ?? ""} onChange={(e) => setEdit({ ...edit, value: e.target.value })}
              className="w-full bg-transparent py-3 text-sm text-[#12142b] outline-none"
            />
            {edit?.kind === "pct" && <span className="text-sm font-semibold text-[#565b73]">%</span>}
            {edit?.kind === "money" && <span className="text-sm font-semibold text-[#565b73]">CAD</span>}
            {edit?.kind === "hours" && <span className="text-sm font-semibold text-[#565b73]">hrs</span>}
          </div>
          <DialogFooter className="mt-3 gap-2 sm:justify-between">
            <button data-testid="finance-edit-cancel" onClick={() => setEdit(null)} disabled={saving}
              className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Cancel</button>
            <button data-testid="finance-edit-save" onClick={saveEdit} disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActivityPanel() {
  const [feed, setFeed] = useState("admin"); // "admin" | "users"
  const [rows, setRows] = useState(null);

  const load = useCallback((which) => {
    setRows(null);
    adminApi.get(`/activity/${which}`)
      .then((r) => setRows(r.data.items || []))
      .catch(() => setRows([]));
  }, []);
  useEffect(() => { load(feed); }, [feed, load]);

  const fmtWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-CA", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="space-y-4" data-testid="activity-panel">
      <div className="flex gap-1 rounded-xl bg-[#f4f6fb] p-1" data-testid="activity-feed-toggle">
        {[["admin", "Admin & Employees"], ["users", "Users"]].map(([k, l]) => (
          <button key={k} data-testid={`activity-feed-${k}`} onClick={() => setFeed(k)}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${feed === k ? "bg-white text-[#2340b5] shadow-sm" : "text-[#565b73] hover:text-[#12142b]"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6">
        <div className="grid grid-cols-[1.2fr_1fr_1.6fr_1fr_1fr] gap-3 border-b border-[#12142b]/10 pb-2 text-xs font-bold uppercase tracking-wide text-[#565b73] max-md:hidden">
          <span>{feed === "admin" ? "Admin / Employee" : "User"}</span><span>Action</span><span>Details</span><span>Date</span><span>Location</span>
        </div>
        {rows === null ? (
          <p className="py-10 text-center text-sm text-[#565b73]">Loading activity…</p>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-[#565b73]" data-testid="activity-empty">No activity recorded yet.</p>
        ) : rows.map((r) => (
          <div key={r.id || `${r.actor}-${r.at}`} data-testid={`activity-row-${r.id || ""}`}
            className="grid grid-cols-[1.2fr_1fr_1.6fr_1fr_1fr] items-center gap-3 border-b border-[#12142b]/6 py-3 last:border-0 max-md:grid-cols-2">
            <span className="truncate text-sm font-semibold text-[#12142b]">{r.actor || "—"}</span>
            <span className="text-sm"><span className="rounded-md bg-[#2340b5]/8 px-2 py-0.5 text-xs font-bold text-[#2340b5]">{r.action}</span></span>
            <span className="truncate text-xs text-[#565b73]" title={r.detail}>{r.detail || "—"}</span>
            <span className="text-xs text-[#565b73]">{fmtWhen(r.at)}</span>
            <span className="truncate text-xs text-[#565b73]">{r.location || "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  { key: "general", label: "General", icon: Settings2 },
  { key: "security", label: "Security", icon: Lock },
  { key: "finance", label: "Finance", icon: CreditCard },
  { key: "app-update", label: "App Update", icon: Smartphone },
  { key: "geographic", label: "Geographic Control", icon: Globe },
  { key: "content", label: "Content Management", icon: FileText },
  { key: "activity", label: "Activity Logs", icon: History },
];

export default function Settings() {
  const [tab, setTab] = useState("general");
  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Security, coverage and content for your SafeRide admin." />
      <div className="space-y-6 p-8">
        <div className="flex gap-1 border-b border-[#12142b]/8" data-testid="settings-tabs">
          {TABS.map((t) => (
            <button
              key={t.key} data-testid={`settings-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t.key ? "border-[#2340b5] text-[#2340b5]" : "border-transparent text-[#565b73] hover:text-[#12142b]"}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "general" && <GeneralPanel />}
        {tab === "security" && <SecurityPanel />}
        {tab === "finance" && <FinancePanel />}
        {tab === "app-update" && <AppUpdateSettings />}
        {tab === "geographic" && <GeographicPanel />}
        {tab === "content" && <ContentPanel />}
        {tab === "activity" && <ActivityPanel />}
      </div>
    </div>
  );
}

function DeviceList({ items, loading, empty, fmt, onRevoke, testid }) {
  if (loading) return <p className="py-6 text-center text-sm text-[#565b73]">Loading…</p>;
  if (!items.length) return <p className="py-6 text-center text-sm text-[#565b73]" data-testid={`${testid}-empty`}>{empty}</p>;
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((d) => (
        <div key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#12142b]/8 p-3">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#f4f6fb]"><Monitor className="h-4 w-4 text-[#565b73]" /></span>
            <div>
              <p className="flex items-center gap-2 text-sm font-semibold text-[#12142b]">{d.name}{d.current && <span className="rounded-full bg-[#16a34a]/12 px-2 py-0.5 text-[10px] font-semibold text-[#16a34a]">This device</span>}{d.trusted && <CheckCircle2 className="h-3.5 w-3.5 text-[#16a34a]" />}</p>
              <p className="text-xs text-[#565b73]">{[d.os, d.browser, d.ip, d.location].filter(Boolean).join(" · ") || "—"} · {fmt(d.last_active)}</p>
              <p className={`text-xs font-semibold ${d.trusted ? "text-[#16a34a]" : "text-[#b45309]"}`} data-testid={`device-trust-${d.id}`}>
                {d.trusted
                  ? (d.trusted_until ? `Trusted · expires ${new Date(d.trusted_until).toLocaleDateString("en-CA", { month: "short", day: "2-digit", year: "numeric" })}` : "Trusted · no expiry")
                  : "Not trusted · code required on next sign-in"}
              </p>
            </div>
          </div>
          {onRevoke && !d.current && d.trusted && (
            <button data-testid={`revoke-device-${d.id}`} onClick={() => onRevoke(d.id, d.name)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#e4002b]/25 px-3 py-1.5 text-xs font-semibold text-[#e4002b] hover:bg-[#e4002b]/8">
              <Trash2 className="h-3.5 w-3.5" /> Revoke
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
